import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Maximize2, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { caseService, documentService } from '../services/api';
import { folderService } from '../services/api';
import DocumentUpload from '../components/documents/DocumentUpload';
import DocumentList from '../components/documents/DocumentList';
import DocumentPreview from '../components/documents/DocumentPreview';
import FolderTree from '../components/documents/FolderTree';
import NewFolderModal from '../components/documents/NewFolderModal';
import useNotification from '../hooks/useNotification';
import Notification from '../components/common/Notification';
import ConfirmDialog from '../components/common/ConfirmDialog';

const CaseDocumentsPage = () => {
  const { id } = useParams();
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documentsKey, setDocumentsKey] = useState(0);
  const [foldersKey, setFoldersKey] = useState(0);

  // Folder states
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [parentFolderForNew, setParentFolderForNew] = useState(null);
  const [showDeleteFolderConfirm, setShowDeleteFolderConfirm] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);

  // Sort state
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Document preview state
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);

  useEffect(() => {
    const fetchCase = async () => {
      try {
        const response = await caseService.getCase(id);
        setCaseData(response.data);
      } catch (err) {
        console.error('Error fetching case:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCase();
  }, [id]);

  const handleDocumentUpload = useCallback(() => {
    setDocumentsKey(prev => prev + 1);
  }, []);

  const handleSelectFolder = (folder) => {
    setSelectedFolder(folder);
  };

  const handleCreateFolder = (parentFolder) => {
    setParentFolderForNew(parentFolder);
    setEditingFolder(null);
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setParentFolderForNew(null);
    setShowFolderModal(true);
  };

  const handleSaveFolder = async (folderData) => {
    try {
      if (editingFolder) {
        await folderService.updateFolder(editingFolder.id, folderData);
        showSuccess('Folder updated successfully');
      } else {
        await folderService.createFolder(folderData);
        showSuccess('Folder created successfully');
      }
      setFoldersKey(prev => prev + 1);
      setDocumentsKey(prev => prev + 1);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteFolder = (folder) => {
    setFolderToDelete(folder);
    setShowDeleteFolderConfirm(true);
  };

  const confirmDeleteFolder = async () => {
    if (!folderToDelete) return;
    try {
      await folderService.deleteFolder(folderToDelete.id, true);
      showSuccess('Folder deleted successfully');
      if (selectedFolder?.id === folderToDelete.id) {
        setSelectedFolder(null);
      }
      setFoldersKey(prev => prev + 1);
      setDocumentsKey(prev => prev + 1);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to delete folder');
    } finally {
      setShowDeleteFolderConfirm(false);
      setFolderToDelete(null);
    }
  };

  const handleDropDocument = useCallback(async (documentId, folderId) => {
    try {
      await documentService.updateDocument(documentId, { folder_id: folderId });
      const folderName = folderId === null ? 'Unfiled Documents' :
        (selectedFolder?.id === folderId ? selectedFolder.name : 'the folder');
      showSuccess(`Document moved to ${folderName}`);
      setFoldersKey(prev => prev + 1);
      setDocumentsKey(prev => prev + 1);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to move document');
    }
  }, [selectedFolder, showSuccess, showError]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder(column === 'name' ? 'asc' : 'desc');
    }
    setDocumentsKey(prev => prev + 1);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ChevronsUpDown className="h-3 w-3 ml-1 text-gray-400" />;
    return sortOrder === 'asc'
      ? <ChevronUp className="h-3 w-3 ml-1 text-yellow-600" />
      : <ChevronDown className="h-3 w-3 ml-1 text-yellow-600" />;
  };

  const handleDocumentClick = useCallback(async (document) => {
    if (!document.file_size || !document.mime_type) {
      try {
        const response = await documentService.getDocument(document.id);
        setPreviewDocument(response.data);
      } catch (err) {
        console.error('Failed to load document details:', err);
        showError('Failed to load document details');
        return;
      }
    } else {
      setPreviewDocument(document);
    }
    setShowDocumentPreview(true);
  }, [showError]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      <Notification notification={notification} onClose={hideNotification} />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link
            to={`/cases/${id}`}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Maximize2 className="h-5 w-5 text-gray-400" />
            <h1 className="text-lg font-semibold text-gray-900">
              Documents
            </h1>
            {caseData && (
              <span className="text-sm text-gray-500">
                — {caseData.case_number}: {caseData.title}
              </span>
            )}
          </div>
          {selectedFolder && (
            <span className="text-sm text-gray-500">
              / {selectedFolder.id === 'unfiled' ? 'Unfiled Documents' : selectedFolder.name}
            </span>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Folder Sidebar */}
        <div className="w-72 border-r border-gray-200 flex-shrink-0 overflow-y-auto bg-white">
          <FolderTree
            key={foldersKey}
            caseId={id}
            selectedFolder={selectedFolder}
            onSelectFolder={handleSelectFolder}
            onCreateFolder={handleCreateFolder}
            onEditFolder={handleEditFolder}
            onDeleteFolder={handleDeleteFolder}
            onDropDocument={handleDropDocument}
            onDocumentClick={handleDocumentClick}
            onRefresh={() => setDocumentsKey(prev => prev + 1)}
          />
        </div>

        {/* Document Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          <div className="mb-6">
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <FileText className="mr-2 h-5 w-5" />
              {selectedFolder
                ? (selectedFolder.id === 'unfiled' ? 'Unfiled Documents' : selectedFolder.name)
                : 'All Documents'}
            </h2>

            <DocumentUpload
              caseId={id}
              folderId={selectedFolder?.id !== 'unfiled' ? selectedFolder?.id : null}
              onUploadSuccess={handleDocumentUpload}
              multiple={true}
            />
          </div>

          {/* Sort Header */}
          <div className="flex items-center gap-4 px-3 py-2 bg-white border border-gray-200 rounded-lg mb-3 text-xs font-medium text-gray-600">
            <button onClick={() => handleSort('name')} className="flex items-center hover:text-gray-900 min-w-0 flex-1">
              Name <SortIcon column="name" />
            </button>
            <button onClick={() => handleSort('size')} className="flex items-center hover:text-gray-900 flex-shrink-0">
              Size <SortIcon column="size" />
            </button>
            <button onClick={() => handleSort('date')} className="flex items-center hover:text-gray-900 flex-shrink-0">
              Date <SortIcon column="date" />
            </button>
            <button onClick={() => handleSort('uploaded_by')} className="flex items-center hover:text-gray-900 flex-shrink-0">
              Uploaded By <SortIcon column="uploaded_by" />
            </button>
            <button onClick={() => handleSort('type')} className="flex items-center hover:text-gray-900 flex-shrink-0">
              Type <SortIcon column="type" />
            </button>
          </div>

          <DocumentList
            key={documentsKey}
            caseId={id}
            folderId={selectedFolder?.id}
            limit={50}
            showActions={true}
            onArchive={() => setFoldersKey(prev => prev + 1)}
            sortBy={sortBy}
            sortOrder={sortOrder}
          />
        </div>
      </div>

      {/* Modals */}
      {showFolderModal && (
        <NewFolderModal
          isOpen={showFolderModal}
          onClose={() => {
            setShowFolderModal(false);
            setEditingFolder(null);
            setParentFolderForNew(null);
          }}
          onSave={handleSaveFolder}
          caseId={id}
          editingFolder={editingFolder}
          parentFolder={parentFolderForNew}
        />
      )}

      {showDeleteFolderConfirm && (
        <ConfirmDialog
          isOpen={showDeleteFolderConfirm}
          title="Delete Folder"
          message={`Are you sure you want to delete "${folderToDelete?.name}"? Documents inside will be moved to unfiled.`}
          confirmLabel="Delete"
          confirmVariant="danger"
          onConfirm={confirmDeleteFolder}
          onCancel={() => {
            setShowDeleteFolderConfirm(false);
            setFolderToDelete(null);
          }}
        />
      )}

      {showDocumentPreview && previewDocument && (
        <DocumentPreview
          document={previewDocument}
          isOpen={showDocumentPreview}
          onClose={() => {
            setShowDocumentPreview(false);
            setPreviewDocument(null);
          }}
        />
      )}
    </div>
  );
};

export default CaseDocumentsPage;
